import { inplayHomepageUrl } from '~spa/Utils/LocalizedUrls';

/**
 * Collection of functions to generate SEO URLs.
 * @see https://dedsert.atlassian.net/wiki/spaces/DEV/pages/819069469/Multi+lingual+urls
 */

export const SEO_URL_SUFFIX_SPORT = 's';
export const SEO_URL_SUFFIX_SPORT_ANTEPOST = 'ap';
export const SEO_URL_SUFFIX_SPORT_FEATURED = 'sf';
export const SEO_URL_SUFFIX_SPORT_TODAY = 'st';
export const SEO_URL_SUFFIX_SPORT_TOMORROW = 'stm';
export const SEO_URL_SUFFIX_TOURNAMENT = 't';
export const SEO_URL_SUFFIX_TOURNAMENT_OUTRIGHTS = 'to';
export const SEO_URL_SUFFIX_TOURNAMENT_DATE = 'toda';
export const SEO_URL_SUFFIX_CATEGORY = 'cat';
export const SEO_URL_SUFFIX_EVENT = 'e';
export const SEO_URL_SUFFIX_COUPON = 'co';
export const SEO_URL_SUFFIX_COUPON_SPORT = 'cos';
export const SEO_URL_SUFFIX_COUPON_TOURNAMENT = 'cot';
export const SEO_URL_SUFFIX_COMPETITION_SPORT = 'coms';
export const SEO_URL_SUFFIX_COMPETITION_CATEGORY = 'comcat';
export const SEO_URL_SUFFIX_INPLAY_HOMEPAGE = 'ip';
export const SEO_URL_SUFFIX_INPLAY_SCHEDULED = 'ipsche';
export const SEO_URL_SUFFIX_INPLAY_SCHEDULED_DATE = 'ipscheda';
export const SEO_URL_SUFFIX_INPLAY_VIRTUAL = 'ipvirt';
export const SEO_URL_SUFFIX_INPLAY_SPORT = 'ips';
export const SEO_URL_SUFFIX_INPLAY_EVENT = 'ipe';
export const SEO_URL_SUFFIX_BETFEED = 'bf';
export const SEO_URL_SUFFIX_RECENTLY_WON = 'mfywon';
export const SEO_URL_SUFFIX_RECENTLY_BACKED = 'mfyback';
export const SEO_URL_SUFFIX_FOR_TODAY = 'mfytoday';
export const SEO_URL_SUFFIX_BRIGHTBUNDLES = 'bb';
export const SEO_URL_SUFFIX_BET_HISTORY = 'b';
export const SEO_URL_SUFFIX_OPEN_BETS = 'bo';
export const SEO_URL_SUFFIX_SETTLED_BETS = 'bs';
export const SEO_URL_SUFFIX_OLDER_BETS = 'boh';
export const SEO_URL_CONTEXT_SEPARATOR = '/';
export const SEO_URL_ALT_CONTEXT_SEPARATOR = '-';


/**
 * Creates a SEO URL by combining the provided URL path with
 * the context label and the optional resource ID.
 * Returns an absolute path.
 *
 * @param {String} path The URL path
 * @param {String} contextLabel The required context label
 * @param {Number} id The resource ID
 * @param {String} contextSeparator the separator of resource ID after path
 * @returns {String} The SEO URL
 */
function buildSeoUrl(path, contextLabel, id = null, contextSeparator = SEO_URL_CONTEXT_SEPARATOR) {
    let urlPath = path;
    if (typeof urlPath !== 'string' || !urlPath.length) {
        // We need to fallback to "goto" segment if we received invalid path
        // for example: undefined, null, true, false, etc
        urlPath = 'goto';
    }

    if (id !== null && urlPath.includes(`${contextSeparator}${contextLabel}-${id}`)) {
        return urlPath;
    }

    const seoUrl = `/${urlPath.replace(/^\/|\/$/g, '')}${contextSeparator}${contextLabel}`;

    // NOTE: The returned URL must not end with '/'.
    // The '/' is appended by rewriteUrl() (shared/utils/uriAliases.js).
    if (id !== null) {
        return `${seoUrl}-${id}/`;
    }

    return `${seoUrl}/`;
}

/**
 * slugify the entity by removing special characters and replacing spaces with hypen
 *
 * @param {String} entity The URL path
 * @returns {String} The Slug
 */
export const slugifyUrlEntities = (entity) => {
    let filteredEntity = entity.replace(/[&/\\#,+()$~%.'":*?<>{}]/g, '');

    return filteredEntity.replace(/\s+/g, '-').toLowerCase();
};

// Sport URLs
export const seoUrlSport = (path, id) => buildSeoUrl(path, SEO_URL_SUFFIX_SPORT, id);

export const seoUrlSportAntepost = (path, id) => buildSeoUrl(path, SEO_URL_SUFFIX_SPORT_ANTEPOST, id);

export const seoUrlSportFeatured = (path, id) => buildSeoUrl(path, SEO_URL_SUFFIX_SPORT_FEATURED, id);

export const seoUrlSportToday = (path, id) => buildSeoUrl(path, SEO_URL_SUFFIX_SPORT_TODAY, id);

export const seoUrlSportTomorrow = (path, id) => buildSeoUrl(path, SEO_URL_SUFFIX_SPORT_TOMORROW, id);

// Tournament URLs
export const seoUrlTournament = (path, id) => buildSeoUrl(path, SEO_URL_SUFFIX_TOURNAMENT, id, SEO_URL_ALT_CONTEXT_SEPARATOR);

export const seoUrlTournamentOutrights = (path, id) => buildSeoUrl(path, SEO_URL_SUFFIX_TOURNAMENT_OUTRIGHTS, id, SEO_URL_ALT_CONTEXT_SEPARATOR);

export const seoUrlTournamentDate = (path, id) => buildSeoUrl(path, SEO_URL_SUFFIX_TOURNAMENT_DATE, id, SEO_URL_ALT_CONTEXT_SEPARATOR);

// Category URLs
export const seoUrlCategory = (path, id) => buildSeoUrl(path, SEO_URL_SUFFIX_CATEGORY, id);

// Event URLs
export const seoUrlEvent = (path, id) => buildSeoUrl(path, SEO_URL_SUFFIX_EVENT, id, SEO_URL_ALT_CONTEXT_SEPARATOR);

// Coupons URLs
export const seoUrlCoupon = (path, id) => buildSeoUrl(path, SEO_URL_SUFFIX_COUPON, id);

export const seoUrlCouponSport = (path, id) => buildSeoUrl(path, SEO_URL_SUFFIX_COUPON_SPORT, id);

export const seoUrlCouponTournament = (path, id) => buildSeoUrl(path, SEO_URL_SUFFIX_COUPON_TOURNAMENT, id, SEO_URL_ALT_CONTEXT_SEPARATOR);

// Competitions URLs
export const seoUrlCompetitionSport = (path, id) => buildSeoUrl(path, SEO_URL_SUFFIX_COMPETITION_SPORT, id);

export const seoUrlCompetitionCategory = (path, id) => buildSeoUrl(path, SEO_URL_SUFFIX_COMPETITION_CATEGORY, id);

// Inplay URLs
export const seoUrlInplay = () => buildSeoUrl(inplayHomepageUrl(), SEO_URL_SUFFIX_INPLAY_HOMEPAGE);

export const seoUrlInplayScheduled = (path) => buildSeoUrl(path, SEO_URL_SUFFIX_INPLAY_SCHEDULED);

export const seoUrlInplayScheduledDate = (path, date) => buildSeoUrl(path, SEO_URL_SUFFIX_INPLAY_SCHEDULED_DATE, date);

export const seoUrlInplayVirtual = (path) => buildSeoUrl(path, SEO_URL_SUFFIX_INPLAY_VIRTUAL);

export const seoUrlInplaySport = (sportSlug, id) => buildSeoUrl(`${inplayHomepageUrl()}/${sportSlug}`, SEO_URL_SUFFIX_INPLAY_SPORT, id);

export const seoUrlInplayEvent = (path, id) => buildSeoUrl(path, SEO_URL_SUFFIX_INPLAY_EVENT, id, SEO_URL_ALT_CONTEXT_SEPARATOR);

// BetFeed
export const seoUrlBetfeed = (path) => buildSeoUrl(path, SEO_URL_SUFFIX_BETFEED);

// Made For You
export const seoUrlRecentlyWon = (path) => buildSeoUrl(path, SEO_URL_SUFFIX_RECENTLY_WON);

export const seoUrlRecentlyBacked = (path) => buildSeoUrl(path, SEO_URL_SUFFIX_RECENTLY_BACKED);

export const seoUrlForToday = (path) => buildSeoUrl(path, SEO_URL_SUFFIX_FOR_TODAY);

// Bright Bundles
export const seoUrlBrightBundles = (path) => buildSeoUrl(path, SEO_URL_SUFFIX_BRIGHTBUNDLES);

// Bet History
export const seoUrlBetHistory = (path) => buildSeoUrl(path, SEO_URL_SUFFIX_BET_HISTORY);

export const seoUrlOpenBets = (path) => buildSeoUrl(path, SEO_URL_SUFFIX_OPEN_BETS);

export const seoUrlSettledBets = (path) => buildSeoUrl(path, SEO_URL_SUFFIX_SETTLED_BETS);

export const seoUrlOlderBets = (path) => buildSeoUrl(path, SEO_URL_SUFFIX_OLDER_BETS);
